<script>
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import { addObject, removeObject, findBy } from '@shell/utils/array';
import { sortBy } from '@shell/utils/sort';
import findIndex from 'lodash/findIndex';
import { ExtensionPoint, TabLocation } from '@shell/core/types';
import { getApplicableExtensionEnhancements } from '@shell/core/plugin-helpers';
import Tab from '@shell/components/Tabbed/Tab';
import { computed, ref, useId, useTemplateRef } from 'vue';
import { useIsInResourceDetailDrawer } from '@shell/components/Drawer/ResourceDetailDrawer/composables';
import { useIsInResourceDetailPage } from '@shell/composables/resourceDetail';
import { useIsInResourceCreatePage, useIsInResourceEditPage } from '@shell/composables/cruResource';
import { useInSummary } from '@shell/components/TableOfContents/composables';

export default {
  name: 'Tabbed',

  components: { Tab },

  emits: ['changed', 'addTab', 'removeTab'],

  props: {
    defaultTab: {
      type:    String,
      default: null,
    },

    sideTabs: {
      type:    Boolean,
      default: false
    },

    hideSingleTab: {
      type:    Boolean,
      default: false
    },

    showTabsAddRemove: {
      type:    Boolean,
      default: false
    },

    // whether or not to scroll to the top of the new tab on tab change. This is particularly ugly with side tabs
    scrollOnChange: {
      type:    Boolean,
      default: false
    },

    useHash: {
      type:    Boolean,
      default: true,
    },

    noContent: {
      type:    Boolean,
      default: false,
    },

    // Remove padding and box-shadow
    flat: {
      type:    Boolean,
      default: false,
    },

    tabsOnly: {
      type:    Boolean,
      default: false,
    },

    resource: {
      type:    Object,
      default: () => {}
    },

    showExtensionTabs: {
      type:    Boolean,
      default: true,
    },

    extensionParams: {
      type:    Object,
      default: null,
    },

    /**
     * Inherited global identifier prefix for tests
     * Define a term based on the parent component to avoid conflicts on multiple components
     */
    componentTestid: {
      type:    String,
      default: 'tabbed'
    },

    removeBorders: {
      type:    Boolean,
      default: false,
    },

    /**
     * title is NOT displayed within the Tabbed component itself
     * this prop is used to determine a label to use for the set of tabs in the table of contents component
     * if a title is not provided a random string will be used
     * components using the table of contents may exclude tabbed and only show tab components, too
     */
    title: {
      type:    String,
      default: null,
    },

    /**
     * id of a tab panel that lives OUTSIDE of this component.
     *
     * Normally each Tab renders its own `role="tabpanel"` and the tab's `aria-controls` points at it.
     * Some consumers (e.g. `tabsOnly` usages) render the tab content themselves, elsewhere in the page.
     * In that case pass the id of that container here so the tabs stay programmatically associated
     * with the content they actually control.
     */
    externalPanelId: {
      type:    String,
      default: null,
    }
  },

  provide() {
    const tabs = this.tabs;

    return {
      select:      this.select,
      sideTabs:    this.sideTabs,
      instanceUid: this.instanceUid,

      addTab(tab) {
        const existing = findBy(tabs, 'name', tab.name);

        if ( existing ) {
          removeObject(tabs, existing);
        }

        addObject(tabs, tab);
      },

      removeTab(tab) {
        removeObject(tabs, tab);
      }
    };
  },

  data() {
    const location = this.getInitialTabLocation();
    let extensionTabs = this.showExtensionTabs ? getApplicableExtensionEnhancements(this, ExtensionPoint.TAB, location, this.$route, this, this.extensionParams) || [] : [];
    const legacyExtensionTabs = this.showExtensionTabs ? getApplicableExtensionEnhancements(this, ExtensionPoint.TAB, TabLocation.RESOURCE_DETAIL, this.$route, this, this.extensionParams) || [] : [];

    if (!extensionTabs.length) {
      // Support legacy tabs for RESOURCE_DETAIL location
      extensionTabs = legacyExtensionTabs;
    }

    const parsedExtTabs = extensionTabs.map((item) => {
      return {
        ...item,
        active: false
      };
    });

    return {
      tabs:          [...parsedExtTabs],
      extensionTabs: parsedExtTabs,
      activeTabName: null,
      tabRefs:       {}
    };
  },

  computed: {
    // keep the tabs list ordered for dynamic tabs
    sortedTabs() {
      return sortBy(this.tabs, ['weight:desc', 'labelDisplay', 'name']);
    },

    // hide tabs based on tab count IF flag is active
    hideTabs() {
      return this.hideSingleTab && this.sortedTabs.length === 1;
    },
  },

  setup(props) {
    const instanceUid = useId();
    const isInResourceDetailDrawer = ref(useIsInResourceDetailDrawer());
    const isInResourceDetailPage = ref(useIsInResourceDetailPage());
    const isInResourceEditPage = ref(useIsInResourceEditPage());
    const isInResourceCreatePage = ref(useIsInResourceCreatePage());
    const tabbedSummarizedContainer = useTemplateRef('tabbed-summarized-container');
    const { summary } = useInSummary({
      label:      computed(() => props.title ?? ''),
      scrollTo:   () => tabbedSummarizedContainer.value?.scrollIntoView(true),
      elementRef: tabbedSummarizedContainer,
    });

    return {
      instanceUid, isInResourceDetailDrawer, isInResourceDetailPage, isInResourceEditPage, isInResourceCreatePage, summary
    };
  },

  watch: {
    sortedTabs(tabs) {
      const {
        defaultTab,
        useHash
      } = this;
      const activeTab = tabs.find((t) => t.active);

      const hash = useHash ? this.$route.hash : undefined;
      const windowHash = useHash ? hash.slice(1) : undefined;
      const windowHashTabMatch = tabs.find((t) => t.name === windowHash && !t.active);
      const firstTab = head(tabs) || null;

      if (isEmpty(activeTab)) {
        if (useHash && !isEmpty(windowHashTabMatch)) {
          this.select(windowHashTabMatch.name);
        } else if (!isEmpty(defaultTab) && !isEmpty(tabs.find((t) => t.name === defaultTab))) {
          this.select(defaultTab);
        } else if (firstTab?.name) {
          this.select(firstTab.name);
        }
      } else if (useHash && activeTab?.name === windowHash) {
        this.select(activeTab.name);
      }
    },
    '$route.hash'() {
      if ( this.useHash ) {
        this.hashChange();
      }
    }
  },

  methods: {
    tabButtonId(name) {
      return `tab-${ this.instanceUid }-${ name }`;
    },

    getInitialTabLocation() {
      if (this.isInResourceEditPage) {
        return TabLocation.RESOURCE_EDIT_PAGE;
      } else if (this.isInResourceDetailDrawer) {
        return TabLocation.RESOURCE_SHOW_CONFIGURATION;
      } else if (this.isInResourceDetailPage) {
        return TabLocation.RESOURCE_DETAIL_PAGE;
      } else if (this.isInResourceCreatePage) {
        return TabLocation.RESOURCE_CREATE_PAGE;
      } else {
        return TabLocation.OTHER;
      }
    },
    hasErrorIcon(tab) {
      return tab.displayAlertIcon || (tab.error && !tab.active);
    },
    hashChange() {
      if (this.scrollOnChange) {
        const scrollable = document.getElementsByTagName('main')[0];

        if (scrollable) {
          scrollable.scrollTop = 0;
        }
      }

      this.select(this.$route.hash);
    },

    find(name) {
      return this.sortedTabs.find((x) => x.name === name );
    },

    select(name/* , event */) {
      const { sortedTabs } = this;

      const cleanName = name.replace('#', '');
      const selected = this.find(cleanName);
      const hashName = `#${ cleanName }`;

      if ( !selected || selected.disabled) {
        return;
      }
      /**
       * Exclude logic with URL anchor (hash) for projects without routing logic (vue-router)
       */
      if ( this.useHash ) {
        const currentRoute = this.$router.currentRoute._value;
        const routeHash = currentRoute.hash;

        if (this.useHash && routeHash !== hashName) {
          const kurrentRoute = { ...currentRoute };

          kurrentRoute.hash = hashName;

          this.$router.replace(kurrentRoute);
        }
      }

      for ( const tab of sortedTabs ) {
        tab.active = (tab.name === selected.name);
      }

      this.$emit('changed', {
        tab:          selected,
        selectedName: selected.name,
        tabButtonId:  this.tabButtonId(selected.name)
      });
      this.activeTabName = selected.name;
    },

    selectNext(direction) {
      const { sortedTabs } = this;

      if (!sortedTabs.length) {
        return;
      }

      const currentIdx = sortedTabs.findIndex((x) => x.active);
      const nextIdx = getCyclicalIdx(currentIdx, direction, sortedTabs.length);

      this.selectAndFocus(sortedTabs[nextIdx].name);

      function getCyclicalIdx(currentIdx, direction, tabsLength) {
        const nxt = currentIdx + direction;

        if (nxt >= tabsLength) {
          return 0;
        } else if (nxt < 0) {
          return tabsLength - 1;
        } else {
          return nxt;
        }
      }
    },

    /**
     * Home/End keyboard support, as per the ARIA authoring practices for tabs
     */
    selectEdge(edge) {
      const { sortedTabs } = this;

      if (!sortedTabs.length) {
        return;
      }

      const tab = edge === 'first' ? sortedTabs[0] : sortedTabs[sortedTabs.length - 1];

      this.selectAndFocus(tab.name);
    },

    /**
     * Move both selection and keyboard focus to a tab.
     *
     * Focus has to follow selection because the tabs use a roving tabindex - only the active tab is
     * reachable with `tabindex="0"`, so leaving focus behind would strand it on an unreachable element.
     */
    selectAndFocus(name) {
      this.select(name);

      this.$nextTick(() => {
        this.tabRefs[name]?.focus();
      });
    },

    tabAddClicked() {
      const activeTabIndex = findIndex(this.tabs, (tab) => tab.active);

      this.$emit('addTab', activeTabIndex);
    },

    tabRemoveClicked() {
      const activeTabIndex = findIndex(this.tabs, (tab) => tab.active);

      this.$emit('removeTab', activeTabIndex);
    },
  },
};
</script>

<template>
  <div
    ref="tabbed-summarized-container"
    class="tabbed-container"
    :class="{
      'side-tabs': !!sideTabs,
      'tabs-only': tabsOnly,
      'remove-borders': removeBorders
    }"
    :data-testid="componentTestid"
  >
    <div
      v-if="!hideTabs"
      class="tabs"
      :class="{'clearfix':!sideTabs, 'vertical': sideTabs, 'horizontal': !sideTabs, 'remove-borders': removeBorders}"
    >
      <ul
        ref="tablist"
        role="tablist"
        class="tab-list"
        :data-testid="`${componentTestid}-block`"
        :aria-orientation="sideTabs ? 'vertical' : 'horizontal'"
      >
        <!-- This is the tabs link... tabs appear here because they are injected from the "Tab" component -->
        <li
          v-for="tab in sortedTabs"
          :key="tab.name"
          :data-testid="tab.name"
          role="presentation"
          :class="{tab: true, active: tab.active, disabled: tab.disabled, error: (tab.error)}"
        >
          <a
            :id="tabButtonId(tab.name)"
            :ref="(el) => { if (el) tabRefs[tab.name] = el; }"
            :data-testid="`btn-${tab.name}`"
            :aria-controls="externalPanelId || `${instanceUid}-${tab.name}`"
            :aria-selected="!!tab.active"
            :aria-disabled="tab.disabled ? 'true' : undefined"
            :aria-label="tab.labelDisplay || ''"
            role="tab"
            :tabindex="tab.active ? '0' : '-1'"
            @click.prevent="select(tab.name, $event)"
            @keydown.enter.prevent="select(tab.name, $event)"
            @keydown.space.prevent="select(tab.name, $event)"
            @keydown.right.prevent="selectNext(1)"
            @keydown.left.prevent="selectNext(-1)"
            @keydown.down.prevent="selectNext(1)"
            @keydown.up.prevent="selectNext(-1)"
            @keydown.home.prevent="selectEdge('first')"
            @keydown.end.prevent="selectEdge('last')"
          >
            <i
              v-if="tab.labelIcon"
              :class="`tab-label-icon icon ${tab.labelIcon}`"
            />
            <span>
              {{ tab.labelDisplay }}
            </span>
            <span
              v-if="tab.badge"
              class="tab-badge"
            >{{ tab.badge }}</span>
            <i
              v-if="hasErrorIcon(tab)"
              v-clean-tooltip="tab.errorIconTooltip || t('validation.tab')"
              class="conditions-alert-icon icon-error"
            />
          </a>
        </li>
      </ul>
      <div
        v-if="sideTabs && !sortedTabs.length"
        class="tab disabled"
        role="presentation"
      >
        <a
          href="#"
          @click.prevent
        >(None)</a>
      </div>
      <!-- A tablist may only own tabs, so the add/remove controls go outside the ul -->
      <div
        v-if="sideTabs && showTabsAddRemove"
        class="tab-list-footer"
        role="presentation"
      >
        <div class="tab-list-footer-controls">
          <button
            type="button"
            class="btn bg-transparent"
            data-testid="tab-list-add"
            :aria-label="t('tabs.addItem')"
            @click="tabAddClicked"
          >
            <i class="icon icon-plus" />
          </button>
          <button
            type="button"
            class="btn bg-transparent"
            :disabled="!sortedTabs.length"
            data-testid="tab-list-remove"
            :aria-label="t('tabs.removeItem')"
            @click="tabRemoveClicked"
          >
            <i class="icon icon-minus" />
          </button>
        </div>
      </div>
      <slot name="tab-row-extras" />
    </div>
    <div
      :class="{
        'tab-container': !!tabs.length || !!sideTabs,
        'no-content': noContent,
        'tab-container--flat': !!flat,
      }"
    >
      <!-- This is where "normal" tab content goes... -->
      <slot />
      <!-- Extension tabs content goes here... -->
      <Tab
        v-for="tab, i in extensionTabs"
        :key="`${tab.name}${i}`"
        :name="tab.name"
        :label="tab.label"
        :label-key="tab.labelKey"
        :label-icon="tab.labelIcon"
        :weight="tab.weight"
        :tooltip="tab.tooltip"
        :show-header="tab.showHeader"
        :display-alert-icon="tab.displayAlertIcon"
        :error="tab.error"
        :error-icon-tooltip="tab.errorIconTooltip"
        :badge="tab.badge"
      >
        <component
          :is="tab.component"
          :resource="resource"
          @select="select(tab.name)"
        />
      </Tab>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tabbed-container {
  min-width: fit-content;
}

.tabs {
  .tab-list {
    list-style-type: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: inherit;
  }

  &.horizontal {
    border: solid thin var(--border);
    border-bottom: 0;
    display: flex;
    flex-direction: row;

    &.remove-borders {
      border: none;

      + .tab-container {
        border: none;
        border-top: 1px solid var(--border);
        padding: 0;
        padding-top: 24px;
      }
    }

    + .tab-container {
      border: solid thin var(--border);
    }

    .tab.active {
      border-bottom: solid 2px var(--active, var(--primary));
    }
  }

  .tab {
    position: relative;
    float: left;
    padding: 0 4px 0 4px;
    cursor: pointer;

    A {
      display: flex;
      align-items: center;
      padding: 10px 15px;

      &:hover {
        text-decoration: none;
        span {
          text-decoration: underline;
        }
      }

      // The tab itself is the focusable element now (roving tabindex), so the focus ring lives here
      // rather than on the tablist.
      &:focus-visible {
        @include focus-outline;
        outline-offset: -2px;

        span {
          text-decoration: underline;
        }
      }
    }

    .conditions-alert-icon {
      color: var(--error);
      padding-left: 4px;
      margin-left: auto;
    }

    &:last-child {
      padding-right: 0;
    }

    &.active {
      > A {
        color: var(--active, var(--primary));
        text-decoration: none;
      }
    }

    &.error {
      & A > .icon-error {
        color: var(--error);
      }
    }

    .tab-label-icon {
      margin-right: 8px;
    }

    .tab-badge {
      margin-left: 5px;
      background-color: var(--link);
      color: #fff;
      border-radius: 6px;
      padding: 1px 7px;
      font-size: 11px;
    }
  }
}

.tab-container {
  padding: 20px;

  &.no-content {
    padding: 0 0 3px 0;
  }

  // Example case: Tabbed component within a tabbed component
  &--flat {
    padding: 0;

    .side-tabs {
      box-shadow: unset;
    }
  }
}

.tabs-only {
  margin-bottom: 20px;

  .tab-container {
    display: none;
  }

  .tabs {
    border: 0;
    border-bottom: 2px solid var(--border);
  }
}

.side-tabs {
  display: flex;
  box-shadow: 0 0 20px var(--shadow);
  border-radius: calc(var(--border-radius) * 2);
  background-color: var(--tabbed-sidebar-bg);

  .tab-container {
    padding: 20px;
  }

  & .tabs {
    width: $sideways-tabs-width;
    min-width: $sideways-tabs-width;
    display: flex;
    flex: 1 0;
    flex-direction: column;

    // &.vertical {
    //   .tab.active {
    //     background-color: var(--tabbed-container-bg);
    //   }
    // }

    & .tab {
      width: 100%;
      border-left: solid 5px transparent;

      &.toggle A {
        color: var(--active, var(--primary));
      }

      A {
        color: var(--link, var(--primary));
      }

      &.active {
        background-color: var(--body-bg);
        border-left: solid 5px var(--active, var(--primary));

        & A {
          color: var(--input-label);
        }
      }

      &.disabled {
        background-color: var(--disabled-bg);

        & A {
          color: var(--disabled-text);
          text-decoration: none;
        }
      }
    }
    .tab-list-footer {
      list-style: none;
      padding: 0;
      margin: auto 0 0;
      z-index: z-index('default');

      .tab-list-footer-controls {
        display: flex;
        flex: 1;

        .btn {
          flex: 1 1;
          display: flex;
          justify-content: center;

          &:focus-visible {
            @include focus-outline;
          }
        }

        button:first-of-type {
          border-top: solid 1px var(--border);
          border-right: solid 1px var(--border);
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
        button:last-of-type {
          border-top: solid 1px var(--border);
          border-top-right-radius: 0;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
    }
  }

  &

  .tab-container {
    width: calc(100% - #{$sideways-tabs-width});
    flex-grow: 1;
    background-color: var(--body-bg);
  }
}
</style>
